import { Component, lazy, Suspense } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { Toaster } from "sonner";
import { TooltipProvider } from "@headerhawk/ui";
import { AuthProvider } from "@/components/providers/auth-provider";
import { QueryProvider } from "@/components/providers/query-provider";
import { ThemeProvider } from "@/components/providers/theme-provider";
import { RequireAuth } from "@/components/providers/require-auth";
import { FullScreenLoader } from "@/components/ui/loader";
import { ImpersonationGate } from "@/components/impersonation/impersonation-gate";
import { DemoBanner } from "@/components/demo/demo-banner";
import { isDemoTab } from "@/lib/auth/impersonation";
import { gateLoaderLabel } from "@/lib/demo/session";
import { DashboardLayout } from "@/routes/dashboard/layout";
import { SectionLayout } from "@/components/layout/section-layout";
import { HeadersLayout } from "@/routes/dashboard/headers/layout";
import { SettingsLayout } from "@/routes/dashboard/settings/layout";
import { apiKeysEnabled } from "@/lib/feature-flags";
import { OnboardingLayout } from "@/routes/onboarding/layout";
import { LegacyStepRoute, LegacyTierRoute } from "@/routes/onboarding/legacy";

const LoginPage = lazy(() =>
  import("@/routes/login").then((m) => ({ default: m.LoginPage })),
);
const SignupPage = lazy(() =>
  import("@/routes/signup").then((m) => ({ default: m.SignupPage })),
);
const AlertEmailConfirmationPage = lazy(() =>
  import("@/routes/alert-email-confirmation").then((m) => ({
    default: m.AlertEmailConfirmationPage,
  })),
);
const DashboardPage = lazy(() =>
  import("@/routes/dashboard").then((m) => ({ default: m.DashboardPage })),
);
const IssuesPage = lazy(() =>
  import("@/routes/dashboard/policy/issues").then((m) => ({
    default: m.IssuesPage,
  })),
);
const PolicyChangesPage = lazy(() =>
  import("@/routes/dashboard/policy/changes").then((m) => ({
    default: m.PolicyChangesPage,
  })),
);
const PolicyDestinationsPage = lazy(() =>
  import("@/routes/dashboard/policy/destinations").then((m) => ({
    default: m.PolicyDestinationsPage,
  })),
);
const PolicyFramesPage = lazy(() =>
  import("@/routes/dashboard/policy/frames").then((m) => ({
    default: m.PolicyFramesPage,
  })),
);
const PolicyDraftPage = lazy(() =>
  import("@/routes/dashboard/policy/draft").then((m) => ({
    default: m.PolicyDraftPage,
  })),
);
const ReportsExplorerPage = lazy(() =>
  import("@/routes/dashboard/reports-explorer").then((m) => ({
    default: m.ReportsExplorerPage,
  })),
);
const DeprecationsPage = lazy(() =>
  import("@/routes/dashboard/signals/deprecations/page").then((m) => ({
    default: m.DeprecationsPage,
  })),
);
const ScriptsPage = lazy(() =>
  import("@/routes/dashboard/scripts/page").then((m) => ({
    default: m.ScriptsPage,
  })),
);
const ReliabilityPage = lazy(() =>
  import("@/routes/dashboard/signals/reliability").then((m) => ({
    default: m.ReliabilityPage,
  })),
);
const MonitoredPagesPage = lazy(() =>
  import("@/routes/dashboard/headers/pages").then((m) => ({
    default: m.MonitoredPagesPage,
  })),
);
const HeaderChangesPage = lazy(() =>
  import("@/routes/dashboard/headers/changes").then((m) => ({
    default: m.HeaderChangesPage,
  })),
);
const MonitoredPageDetailPage = lazy(() =>
  import("@/routes/dashboard/headers/page-detail").then((m) => ({
    default: m.MonitoredPageDetailPage,
  })),
);
const PciDssPage = lazy(() =>
  import("@/routes/dashboard/compliance/pci-dss").then((m) => ({
    default: m.PciDssPage,
  })),
);
const AlertsPage = lazy(() =>
  import("@/routes/dashboard/alerts/page").then((m) => ({
    default: m.AlertsPage,
  })),
);
const GeneralSettingsPage = lazy(() =>
  import("@/routes/dashboard/settings").then((m) => ({
    default: m.GeneralSettingsPage,
  })),
);
const TeamSettingsPage = lazy(() =>
  import("@/routes/dashboard/settings/team").then((m) => ({
    default: m.TeamSettingsPage,
  })),
);
const BillingPage = lazy(() =>
  import("@/routes/dashboard/settings/billing").then((m) => ({
    default: m.BillingPage,
  })),
);
const SitesSettingsPage = lazy(() =>
  import("@/routes/dashboard/settings/sites").then((m) => ({
    default: m.SitesSettingsPage,
  })),
);
const AuthenticationSettingsPage = lazy(() =>
  import("@/routes/dashboard/settings/authentication").then((m) => ({
    default: m.AuthenticationSettingsPage,
  })),
);
const ApiKeysSettingsPage = lazy(() =>
  import("@/routes/dashboard/settings/api-keys").then((m) => ({
    default: m.ApiKeysSettingsPage,
  })),
);
const IntegrationsSettingsPage = lazy(() =>
  import("@/routes/dashboard/settings/integrations").then((m) => ({
    default: m.IntegrationsSettingsPage,
  })),
);
const FirstRun = lazy(() =>
  import("@/routes/onboarding").then((m) => ({
    default: m.FirstRun,
  })),
);

interface ChunkErrorBoundaryState {
  hasError: boolean;
}

class ChunkErrorBoundary extends Component<
  { children: ReactNode },
  ChunkErrorBoundaryState
> {
  state: ChunkErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ChunkErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Chunk loading failed:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4">
          <p className="text-muted-foreground">
            Failed to load page. Check your connection and try again.
          </p>
          <button
            className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground hover:bg-primary-hover"
            onClick={() => window.location.reload()}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export function App() {
  return (
    <ThemeProvider defaultTheme="system" storageKey="headerhawk-theme">
      <QueryProvider>
        <AuthProvider>
          <TooltipProvider delayDuration={300}>
            {/* An operator's read-only view of a member (task 394): a banner
                over the app, and an ended page in place of it. A no-op in
                every ordinary tab. */}
            <ImpersonationGate>
              {/* No transitions (task 478): React Router 8 runs every
                  navigation in one by default, which keeps the old page up,
                  live and unmarked while a lazy route's chunk loads, and lets
                  a second setSearchParams overwrite a first that has not
                  committed. Opting in is a redesign of every pending state,
                  not an upgrade. Tests render MemoryRouter the same way. */}
              <BrowserRouter useTransitions={false}>
                {/* The public demo's banner (task 405), inside the router
                    because it names the section on screen. */}
                {isDemoTab() && <DemoBanner />}
                <Toaster position="top-right" richColors />
                <ChunkErrorBoundary>
                  <Suspense
                    fallback={<FullScreenLoader label={gateLoaderLabel()} />}
                  >
                    <Routes>
                      <Route path="/login" element={<LoginPage />} />
                      <Route path="/signup" element={<SignupPage />} />
                      {/* Public: the person confirming may have no account. */}
                      <Route
                        path="/alert-email-confirmation"
                        element={<AlertEmailConfirmationPage />}
                      />
                      <Route path="/onboarding" element={<OnboardingLayout />}>
                        <Route index element={<FirstRun />} />
                        {/* The steps the first run replaced (task 512). */}
                        <Route path="org" element={<LegacyStepRoute />} />
                        <Route path="tier" element={<LegacyTierRoute />} />
                        <Route path="site" element={<LegacyStepRoute />} />
                        <Route path="setup" element={<LegacyStepRoute />} />
                      </Route>
                      <Route
                        path="/dashboard"
                        element={
                          <RequireAuth>
                            <DashboardLayout />
                          </RequireAuth>
                        }
                      >
                        <Route index element={<DashboardPage />} />
                        {/* The sections of ADR 013. A section's tabs are routes
                          of its own under it, drawn by SectionLayout from
                          @/lib/navigation. */}
                        <Route
                          path="policy"
                          element={<SectionLayout section="policy" />}
                        >
                          <Route index element={<IssuesPage />} />
                          <Route
                            path="changes"
                            element={<PolicyChangesPage />}
                          />
                          <Route
                            path="destinations"
                            element={<PolicyDestinationsPage />}
                          />
                          <Route path="frames" element={<PolicyFramesPage />} />
                          <Route path="draft" element={<PolicyDraftPage />} />
                        </Route>
                        <Route
                          path="explorer"
                          element={<ReportsExplorerPage />}
                        />
                        <Route path="scripts" element={<ScriptsPage />} />
                        {/* Scripts › Integrity (task 375): the one Scripts tab
                          with a route of its own; the rest are ?tab=. */}
                        <Route
                          path="scripts/integrity"
                          element={<ScriptsPage />}
                        />
                        <Route
                          path="signals"
                          element={<SectionLayout section="signals" />}
                        >
                          <Route index element={<ReliabilityPage />} />
                          <Route
                            path="deprecations"
                            element={<DeprecationsPage />}
                          />
                        </Route>
                        {/* Alert emails and webhooks link to these two
                          (task 192); keep the paths in step with
                          apps/alert-delivery/src/templates/change-alert.ts. */}
                        <Route path="headers" element={<HeadersLayout />}>
                          <Route index element={<MonitoredPagesPage />} />
                          <Route
                            path="changes"
                            element={<HeaderChangesPage />}
                          />
                        </Route>
                        <Route
                          path="headers/:pageId"
                          element={<MonitoredPageDetailPage />}
                        />
                        <Route path="alerts" element={<AlertsPage />} />
                        <Route
                          path="compliance"
                          element={<SectionLayout section="compliance" />}
                        >
                          <Route index element={<PciDssPage />} />
                        </Route>
                        <Route path="settings" element={<SettingsLayout />}>
                          <Route index element={<GeneralSettingsPage />} />
                          <Route path="team" element={<TeamSettingsPage />} />
                          <Route path="sites" element={<SitesSettingsPage />} />
                          <Route
                            path="authentication"
                            element={<AuthenticationSettingsPage />}
                          />
                          {apiKeysEnabled() && (
                            <Route
                              path="api-keys"
                              element={<ApiKeysSettingsPage />}
                            />
                          )}
                          <Route
                            path="integrations"
                            element={<IntegrationsSettingsPage />}
                          />
                          <Route path="billing" element={<BillingPage />} />
                        </Route>
                      </Route>
                      <Route
                        path="/"
                        element={<Navigate to="/dashboard" replace />}
                      />
                      <Route
                        path="*"
                        element={<Navigate to="/dashboard" replace />}
                      />
                    </Routes>
                  </Suspense>
                </ChunkErrorBoundary>
              </BrowserRouter>
            </ImpersonationGate>
          </TooltipProvider>
        </AuthProvider>
      </QueryProvider>
    </ThemeProvider>
  );
}
